'use client';

import { NavigableButton } from '@carvana/ds-button';
import Card from '@carvana/ds-card';
import Link from '@carvana/ds-link';
import { icon } from '@carvana/ds-styles';
import { MapProps } from '@vis.gl/react-google-maps';
import { MapPin, Phone } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import GoogleMap from '~/components/dealership/GoogleMap';
import { heliosSizes } from '~/constants/common/resizeBreakpoints';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  useDealershipAddress,
  useDealershipPhones
} from '~/context/dealership/StaticContext';
import useResizeListener from '~/hooks/common/ui/useResizeListener';
import useGoogleMapsDirection from '~/hooks/dealership/useGoogleMapsDirection';
import {
  ContactWrapper,
  MobileSpaceTaker,
  WrapperWithMap
} from './ContactLocation.styles';

export default function ContactLocation() {
  useResizeListener(heliosSizes);

  const isMobile = useIsMobile();
  const address = useDealershipAddress();
  const phones = useDealershipPhones();
  const googleMapsDirections = useGoogleMapsDirection();

  const mapProps: Partial<MapProps> = useMemo(() => {
    const offset = isMobile ? 0.1 : 0.009;
    const newPosition = { lat: 0, lng: 0, ...address.position };

    if (isMobile) {
      // center the map slightly below the pin on mobile
      newPosition.lat -= offset;
    } else {
      // center the map to the left of the pin
      newPosition.lng -= offset;
    }

    return { center: newPosition, zoom: isMobile ? 11 : 14.5 };
  }, [address.position, isMobile]);

  return (
    <div className="relative bg-canvas-default sm:px-32 sm:py-7xl lg:px-[192px]">
      <WrapperWithMap>
        <MobileSpaceTaker />

        <GoogleMap {...mapProps} />

        <Card
          margin="lg"
          className="relative m-auto w-fit bg-background-medium text-text-inverse t-body-sm sm:m-0"
          classes={{ content: 'flex flex-col gap-y-24' }}
        >
          <div className="flex flex-col gap-y-lg">
            <h2 className="t-heading-xl">Test drive today</h2>
            <ContactWrapper>
              <span>
                <MapPin color={icon.inverse} size={12} />
              </span>
              <p className="whitespace-nowrap">
                {address.street1}
                <br />
                {address.city}, {address.state} {address.zip}
              </p>
              <span>
                <Phone color={icon.inverse} size={12} />
              </span>
              <p>
                {phones.map((phone, index) => (
                  <Fragment key={phone.label}>
                    {index > 0 && <br />}
                    {phone.label}:{' '}
                    <Link
                      href={`tel:${phone.number}`}
                      priority="inverse"
                      size="sm"
                      preventVisitedColor
                      data-adobe-id={`click-to-call-${phone.label.toLowerCase()}`}
                      data-analytics-id="Phone number"
                      data-anlytics-attributes={JSON.stringify(phone)}
                      data-sda-event={ShiftDigitalEventName.ClickToCall}
                      data-sda-event-set-prop={`clickToCallDepartment|${phone.label}`}
                      data-testid="phone-number"
                    >
                      {phone.number}
                    </Link>
                  </Fragment>
                ))}
              </p>
            </ContactWrapper>
          </div>

          <NavigableButton
            variant="secondaryInverted"
            size="md"
            width="fit-content"
            href={googleMapsDirections}
            target="_blank"
            rel="noopener noreferrer"
            data-adobe-id="get-directions"
            data-analytics-id="Contact/Location"
            data-anlytics-attributes={JSON.stringify({
              item: 'Get directions button'
            })}
            data-sda-event={ShiftDigitalEventName.GetDirections}
            data-testid="get-directions"
          >
            Get directions
          </NavigableButton>
        </Card>
      </WrapperWithMap>
    </div>
  );
}
